import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { filter, finalize } from 'rxjs';
import { ProjectLogRecord } from '@modules/projects/shared/interfaces/project-log.interface';
import { ProjectLogbookRowView, mapProjectLogRecordToRow } from './project-summary-logbook.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Router } from '@angular/router';
import { ContactController } from '@modules/contacts/shared/controllers/contact.controller';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Config } from '@shared/configs/config';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { SnackBarService } from '@core/services/snackbar.service';
import { ProjectLogNavigationService } from '@modules/projects/shared/services/project-log-navigation.service';

@Component({
  selector: 'project-summary-logbook-comment-modal',
  standalone: true,
  imports: [SharedModule, TranslateModule],
  templateUrl: './components/project-summary-logbook-comment-modal/project-summary-logbook-comment-modal.component.html',
  styleUrls: ['./components/project-summary-logbook-comment-modal/project-summary-logbook-comment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSummaryLogbookCommentModalComponent {
  private readonly dialogRef = inject(MatDialogRef<ProjectSummaryLogbookCommentModalComponent, string>);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly comment = signal('');

  constructor() {
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => this.close());
  }

  protected updateComment(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement | null;
    this.comment.set(textarea?.value || '');
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    this.dialogRef.close(this.comment().trim());
  }
}

@Component({
  selector: 'project-summary-logbook',
  standalone: true,
  imports: [TranslateModule, SharedModule, AngularSvgIconModule],
  templateUrl: './project-summary-logbook.component.html',
  styleUrls: ['./project-summary-logbook.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSummaryLogbookComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectApiService = inject(ProjectAPIService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBarService = inject(SnackBarService);
  private readonly projectLogNavigationService = inject(ProjectLogNavigationService);

  readonly projectId = input<number | null>(null);
  private readonly records = signal<ProjectLogRecord[]>([]);

  protected readonly loading = signal(false);
  protected readonly rows = computed<ProjectLogbookRowView[]>(() =>
    this.records().map((record) => mapProjectLogRecordToRow(record, this.translateService, this.projectId()))
  );

  constructor() {
    effect(() => {
      const projectId = this.projectId();

      if (!projectId) {
        this.records.set([]);
        this.loading.set(false);
        return;
      }

      this.loadProjectLog(projectId);
    }, { allowSignalWrites: true });
  }

  protected openFullLogbook(): void {
    const projectId = this.projectId();
    if (!projectId) return;

    this.router.navigate(['/panel/projects/dashboard', projectId, 'logbook']);
  }

  protected openCommentModal(): void {
    const projectId = this.projectId();
    if (!projectId) return;

    this.dialog.open(ProjectSummaryLogbookCommentModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: 'textarea',
      panelClass: 'project-summary-logbook-comment-modal-panel'
    })
      .afterClosed()
      .pipe(
        filter((comment): comment is string => !!comment?.trim()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((comment) => {
        this.saveComment(projectId, comment.trim());
      });
  }

  protected openActorContact(event: Event, contactId: number | null | undefined): void {
    event.stopPropagation();
    if (!contactId) return;

    const ctrl = new ContactController();
    ctrl.preview(contactId);
  }

  protected openRowAction(row: ProjectLogbookRowView): void {
    if (!row?.action) return;
    this.projectLogNavigationService.open(row.action);
  }

  private loadProjectLog(projectId: number): void {
    this.loading.set(true);

    this.projectApiService
      .getProjectLog(projectId, {
        page: 1,
        records: 3,
        sortField: 'created',
        sortDirection: 'desc'
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.records.set(Array.isArray(response?.records) ? response.records : []);
        },
        error: () => {
          this.records.set([]);
        }
      });
  }

  private saveComment(projectId: number, content: string): void {
    this.projectApiService
      .createProjectLogComment(projectId, { content })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackBarService.success(
            this.translateService.instant('ProjectsDashboard.summary.logbook.saveSuccess')
          );
          this.loadProjectLog(projectId);
        },
        error: () => {
          this.snackBarService.error(
            this.translateService.instant('ProjectsDashboard.summary.logbook.saveError')
          );
        }
      });
  }
}
